/**
 * Investors Hub (Bucket B) — a 4-journey landing. Each card opens one of the
 * investment surfaces (/investors/*). The old apply form now lives on the
 * Investment-Requests page; "Special deals" stays its own top-level nav item.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { fetchFeaturedDevelopers, type FeaturedDeveloper } from '../../services/investorsService';
import licensedProjectsArt from '../../assets/investment/licensed-projects.svg';
import contributionsArt from '../../assets/investment/contributions.svg';
import investmentRequestsArt from '../../assets/investment/investment-requests.svg';
import listInvestmentArt from '../../assets/investment/list-investment.svg';
import { BrandSignature } from '../../components/brand/BrandRule';

export default function InvestorsHubPage() {
  const { i18n } = useTranslation(['common']);
  const isRTL = i18n.dir() === 'rtl';
  const t = (en: string, ar: string) => (isRTL ? ar : en);

  const [developers, setDevelopers] = useState<FeaturedDeveloper[]>([]);
  useEffect(() => {
    fetchFeaturedDevelopers(6).then(setDevelopers).catch(() => setDevelopers([]));
  }, []);

  // The four investment journeys. The accent matches the owner's reference art:
  // construction / gold-check / magnifier / coins.
  const journeys = [
    {
      to: '/investors/licensed-projects',
      art: licensedProjectsArt,
      title: t('Invest in licensed projects', 'الاستثمار في المشاريع المرخّصة'),
      desc: t('Browse licensed projects for sale or rent by area, and see who is investing in each.', 'تصفّح المشاريع المرخّصة للبيع أو الإيجار حسب المنطقة، وشاهد من يستثمر في كل مشروع.'),
    },
    {
      to: '/investors/contributions',
      art: contributionsArt,
      title: t('Real-estate contributions', 'المساهمات العقارية'),
      desc: t('Contribute development funding to licensed projects within the developer’s set range.', 'ساهم بتمويل تطوير المشاريع المرخّصة ضمن الحصة التي حدّدها المطوّر.'),
    },
    {
      to: '/investors/requests',
      art: investmentRequestsArt,
      title: t('Investment requests', 'طلبات الاستثمار العقاري'),
      desc: t('See developers seeking land & buildings to develop, and register your interest.', 'اطّلع على المطوّرين الباحثين عن أراضٍ ومبانٍ للتطوير، وسجّل اهتمامك.'),
    },
    {
      to: '/investors/list',
      art: listInvestmentArt,
      title: t('List for investment', 'اعرض عقارك للاستثمار'),
      desc: t('Offer your property or land to investors - we review it manually and get back to you.', 'اعرض عقارك أو أرضك على المستثمرين - نراجعه يدوياً ونتواصل معك.'),
    },
  ];

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />

      {/* Hero */}
      <header className="relative overflow-hidden bg-[#003930] text-white">
        <div className="relative mx-auto max-w-5xl px-6 py-20 text-center sm:py-24">
          {/* No eyebrow pill over the title (owner, 2026-10-07): the title says it. */}
          <h1 className="font-display text-3xl font-bold leading-tight sm:text-5xl">
            {t('Real-estate investment, four ways', 'الاستثمار العقاري بأربع طرق')}
          </h1>
          <BrandSignature className="mx-auto mt-4" />
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/85 sm:text-lg">
            {t('Invest in licensed projects, contribute to development, browse investment demand, or list your own property for investment.', 'استثمر في مشاريع مرخّصة، ساهم في التطوير، تصفّح طلبات الاستثمار، أو اعرض عقارك للاستثمار.')}
          </p>
        </div>
      </header>

      <main>
        {/* The four journeys */}
        <section className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {journeys.map((j) => (
              <Link
                key={j.to}
                to={j.to}
                className={`group rounded-3xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-[var(--pv-color-accent-soft)] hover:shadow-[0_18px_40px_-18px_rgba(0,77,64,0.32)] ${isRTL ? 'text-right' : ''}`}
              >
                <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white ring-1 ring-[#DDEEEB] shadow-sm">
                  <img src={j.art} alt="" className="h-16 w-16" loading="lazy" />
                </div>
                <h2 className="font-display text-xl font-bold text-[var(--pv-color-primary)] group-hover:text-[#003930]">{j.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{j.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#003930]">
                  {t('Explore', 'استكشف')} <i className={isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured developers */}
        <section id="developers" className="bg-[var(--pv-color-surface-strong)] py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className={`mb-8 flex items-end justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="font-display text-2xl font-bold text-[var(--pv-color-primary)]">{t('Featured developers', 'مطوّرون مميزون')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('Verified developers showcasing live projects.', 'مطوّرون موثّقون يعرضون مشاريع مباشرة.')}</p>
              </div>
              <Link to="/trusted-brokers?type=developer" className="shrink-0 text-sm font-medium text-[#003930] hover:underline">{t('View all', 'عرض الكل')}</Link>
            </div>

            {developers.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-500">
                {t('Verified developers will appear here soon.', 'سيظهر المطوّرون الموثّقون هنا قريباً.')}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {developers.map((d) => {
                  const name = isRTL ? (d.nameAr || d.name) : d.name;
                  const loc = d.headquartersCity || d.region || '';
                  return (
                    <Link key={d.id} to={`/developers/${d.slug}`} className="group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-[var(--pv-color-accent-soft)] hover:shadow-[0_14px_34px_-14px_rgba(0,77,64,0.28)]">
                      <div className="h-24 bg-[#004D40] bg-cover bg-center" style={d.coverUrl ? { backgroundImage: `url(${d.coverUrl})` } : undefined} />
                      <div className={`p-4 ${isRTL ? 'text-right' : ''}`}>
                        <div className={`-mt-10 mb-2 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {d.logoUrl ? (
                            <img src={d.logoUrl} alt={name} className="h-14 w-14 rounded-xl border border-slate-100 bg-white object-cover" />
                          ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#004D40] text-lg font-bold text-white">{name.charAt(0)}</div>
                          )}
                        </div>
                        <h3 className="flex items-center gap-1.5 truncate text-[15px] font-semibold text-slate-900 group-hover:text-[#003930]">
                          {name}{d.isVerified && <i className="ri-verified-badge-fill text-[#004D40]" />}
                        </h3>
                        <div className={`mt-1 flex items-center gap-3 text-xs text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {loc && <span className="flex items-center gap-1"><i className="ri-map-pin-2-line" />{loc}</span>}
                          <span className="flex items-center gap-1"><i className="ri-building-4-line" />{d.projectsCount ?? 0} {t('projects', 'مشروع')}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
